import type { WheelEntry } from '../types/wheel';
import { DEFAULT_WEIGHT } from './weights';
import {
  MAX_ENTRY_IMAGE_DATA_URL_LENGTH,
  MAX_ENTRY_NAME_LENGTH,
} from './wheelData';

/** Files larger than this are rejected before the browser tries to decode them. */
export const MAX_IMAGE_SOURCE_BYTES = 10 * 1024 * 1024;

/**
 * Aggregate budget for image strings attached to one wheel.
 *
 * Data URLs contain only ASCII, so their string length is also a useful,
 * deterministic approximation of their serialized byte cost. Keeping the
 * budget below 1.5 MB leaves room for the rest of a wheel in browser storage.
 */
export const MAX_TOTAL_IMAGE_DATA_URL_LENGTH = 1_500_000;

/** Images are square so every wheel slice can use the same crop calculation. */
export const MAX_WHEEL_IMAGE_DIMENSION = 256;

export const IMAGE_FILE_ACCEPT = 'image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp';

const SUPPORTED_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const SUPPORTED_FILE_EXTENSION = /\.(?:png|jpe?g|webp)$/i;

export type ImageUploadFailureCode =
  | 'entry-limit'
  | 'unsupported-type'
  | 'file-too-large'
  | 'empty-file'
  | 'decode-failed'
  | 'encode-failed'
  | 'image-too-large'
  | 'total-image-limit';

export interface ImageUploadFailure {
  fileName: string;
  code: ImageUploadFailureCode;
  message: string;
}

export interface ImageEntryUploadOptions {
  /** Number of entry slots still available on the wheel. */
  remainingEntries: number;
  /** Sum of the existing entries' imageDataUrl string lengths. */
  existingImageDataLength?: number;
}

export interface ImageEntryUploadResult {
  entries: WheelEntry[];
  skipped: ImageUploadFailure[];
  /** Data-URL characters contributed by the entries in this result. */
  addedImageDataLength: number;
  /** Existing image data plus the entries accepted in this result. */
  totalImageDataLength: number;
}

interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  dispose: () => void;
}

function failure(file: File, code: ImageUploadFailureCode, message: string): ImageUploadFailure {
  return { fileName: file.name || 'Unnamed image', code, message };
}

/**
 * Turn a device filename into the text fallback used by results, exports and
 * assistive technology. The image extension is removed, not arbitrary dots in
 * the filename, and control characters cannot leak into announcements/CSV.
 */
export function imageEntryName(fileName: string, fallbackNumber: number): string {
  const withoutPath = fileName.split(/[\\/]/).pop() ?? '';
  const withoutExtension = withoutPath.replace(SUPPORTED_FILE_EXTENSION, '');
  const withoutControls = Array.from(withoutExtension)
    .filter(character => {
      const code = character.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('');
  const cleaned = withoutControls
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const fallback = `Image ${Math.max(1, fallbackNumber)}`;
  return (cleaned || fallback).slice(0, MAX_ENTRY_NAME_LENGTH);
}

function hasSupportedType(file: File): boolean {
  const mime = file.type.toLowerCase();
  if (mime) return SUPPORTED_MIME_TYPES.has(mime);
  // A few device pickers omit MIME information. The decoder remains the final
  // validation step, so an extension-only candidate cannot inject arbitrary
  // data into an entry.
  return SUPPORTED_FILE_EXTENSION.test(file.name);
}

async function decodeWithImageElement(file: File): Promise<DecodedImage> {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.decoding = 'async';

  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('The browser could not decode this image.'));
      image.src = objectUrl;
    });
    if (!image.naturalWidth || !image.naturalHeight) {
      throw new Error('The decoded image has no dimensions.');
    }
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      dispose: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
}

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      if (!bitmap.width || !bitmap.height) {
        bitmap.close();
        throw new Error('The decoded image has no dimensions.');
      }
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        dispose: () => bitmap.close(),
      };
    } catch {
      // Safari versions and a few platform codecs can fail createImageBitmap
      // while the native image element still decodes the same file correctly.
    }
  }
  return decodeWithImageElement(file);
}

function renderSquare(
  decoded: DecodedImage,
  dimension: number,
): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = dimension;
  canvas.height = dimension;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const cropSize = Math.min(decoded.width, decoded.height);
  const sourceX = (decoded.width - cropSize) / 2;
  const sourceY = (decoded.height - cropSize) / 2;

  // A neutral backing avoids browsers choosing black for transparent pixels
  // when WebP is unavailable and the JPEG fallback is used.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, dimension, dimension);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    decoded.source,
    sourceX,
    sourceY,
    cropSize,
    cropSize,
    0,
    0,
    dimension,
    dimension,
  );
  return canvas;
}

const OUTPUT_QUALITIES = [0.86, 0.74, 0.62, 0.5, 0.4];
const OUTPUT_DIMENSIONS = [256, 224, 192, 160, 128, 96, 64, 48];

function encodeWithinLimit(decoded: DecodedImage): string | null {
  const largest = Math.max(1, Math.min(
    MAX_WHEEL_IMAGE_DIMENSION,
    Math.floor(Math.min(decoded.width, decoded.height)),
  ));
  const dimensions = [
    largest,
    ...OUTPUT_DIMENSIONS.filter(dimension => dimension < largest),
  ];

  for (const dimension of dimensions) {
    const canvas = renderSquare(decoded, dimension);
    if (!canvas) return null;

    for (const mime of ['image/webp', 'image/jpeg'] as const) {
      for (const quality of OUTPUT_QUALITIES) {
        let dataUrl = '';
        try {
          dataUrl = canvas.toDataURL(mime, quality);
        } catch {
          continue;
        }
        // Browsers that do not support WebP may silently return PNG. Do not
        // mistake that generally larger result for the requested encoding.
        if (!dataUrl.startsWith(`data:${mime};base64,`)) continue;
        if (dataUrl.length <= MAX_ENTRY_IMAGE_DATA_URL_LENGTH) return dataUrl;
      }
    }
  }

  return null;
}

/**
 * Validate and normalize device images into WheelEntry records.
 *
 * Files are deliberately processed one at a time. Besides reducing peak memory
 * usage on phones, this preserves the order in which the picker supplied them.
 * Invalid files are reported independently and never prevent valid siblings
 * from being returned.
 */
export async function createImageEntries(
  files: readonly File[],
  options: ImageEntryUploadOptions,
): Promise<ImageEntryUploadResult> {
  const entries: WheelEntry[] = [];
  const skipped: ImageUploadFailure[] = [];
  const remainingEntries = Math.max(0, Math.floor(options.remainingEntries));
  const existingLength = Math.max(0, Math.floor(options.existingImageDataLength ?? 0));
  let totalImageDataLength = Math.min(existingLength, MAX_TOTAL_IMAGE_DATA_URL_LENGTH);

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];

    if (entries.length >= remainingEntries) {
      skipped.push(failure(file, 'entry-limit', 'The wheel has no remaining entry slots.'));
      continue;
    }
    if (!hasSupportedType(file)) {
      skipped.push(failure(file, 'unsupported-type', 'Use a PNG, JPEG or WebP image.'));
      continue;
    }
    if (file.size === 0) {
      skipped.push(failure(file, 'empty-file', 'The image file is empty.'));
      continue;
    }
    if (file.size > MAX_IMAGE_SOURCE_BYTES) {
      skipped.push(failure(file, 'file-too-large', 'The source image is larger than 10 MB.'));
      continue;
    }

    let decoded: DecodedImage | null = null;
    try {
      decoded = await decodeImage(file);
      const imageDataUrl = encodeWithinLimit(decoded);
      if (!imageDataUrl) {
        skipped.push(failure(
          file,
          'image-too-large',
          `The image could not be compressed below ${MAX_ENTRY_IMAGE_DATA_URL_LENGTH.toLocaleString()} characters.`,
        ));
        continue;
      }
      if (totalImageDataLength + imageDataUrl.length > MAX_TOTAL_IMAGE_DATA_URL_LENGTH) {
        skipped.push(failure(
          file,
          'total-image-limit',
          'Adding this image would exceed the wheel image-storage limit.',
        ));
        continue;
      }

      entries.push({
        name: imageEntryName(file.name, index + 1),
        weight: DEFAULT_WEIGHT,
        imageDataUrl,
      });
      totalImageDataLength += imageDataUrl.length;
    } catch {
      skipped.push(failure(file, 'decode-failed', 'The browser could not decode this image.'));
    } finally {
      decoded?.dispose();
    }
  }

  return {
    entries,
    skipped,
    addedImageDataLength: totalImageDataLength - Math.min(existingLength, MAX_TOTAL_IMAGE_DATA_URL_LENGTH),
    totalImageDataLength,
  };
}
